import { analytics } from '@/services/analytics/firebase';
import { appConfig } from '@/app/config';
import { logDebug, logWarn } from '@personal-portfolio/common-utils';
import { logEvent, Analytics, setUserId } from 'firebase/analytics';
import {
  performComprehensiveBotAnalysis,
  BOT_USER_AGENT_PREFIX_LENGTH,
  type ComprehensiveBotDetectionResult,
} from '@/services/analytics/bot-detection';
import { getUserId, clearUserId } from '@/services/analytics/user-id';

/** Analytics event structure for portfolio tracking. */
export interface AnalyticsEvent {
  event_name: string;
  event_parameters?: Record<string, string | number | boolean | undefined>;
}

// Feature flags for analytics behavior (toggles/config that can be changed)
interface AnalyticsFeatureFlags {
  // Enable bot detection filtering (blocks events from detected bots)
  botDetectionEnabled: boolean;
  // Allow repeated bot_detected events (for debugging, disabled in production)
  allowRepeatedBotDetectionEvents: boolean;
}

// Analytics service class
export class AnalyticsService {
  private static instance: AnalyticsService;
  private isInitialized = false;
  // Single shared init promise: concurrent callers await the same attempt,
  // and once settled (even when analytics is unsupported/unconfigured) it
  // never re-runs per event.
  private initPromise: Promise<boolean> | null = null;
  private featureFlags: AnalyticsFeatureFlags;
  // Runtime environment context (not a feature flag, but configuration)
  private readonly environment: 'development' | 'production';
  private userId: string | null = null;

  private constructor() {
    // Resolved once here rather than per event: appConfig.isDev is fixed at
    // build time, and every tracked event stamps this value.
    const isDevelopment = appConfig.isDev;

    this.environment = isDevelopment ? 'development' : 'production';

    this.featureFlags = {
      botDetectionEnabled: true,
      allowRepeatedBotDetectionEvents: isDevelopment,
    };
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  public initialize(): Promise<boolean> {
    this.initPromise ??= (async () => {
      // Warm the persistent user ID during browser idle time so hot paths
      // (e.g. the resume popup submit) read it from localStorage synchronously.
      this.warmUserIdAtIdle();

      try {
        const analyticsInstance = await analytics;
        if (analyticsInstance) {
          // Set user ID before marking as initialized
          // This ensures user ID is set for all subsequent events
          this.setUserId(analyticsInstance);

          this.isInitialized = true;
          logDebug('Firebase Analytics initialized successfully', {
            userId: this.userId || 'none',
          });
        }
      } catch (error) {
        logWarn('Failed to initialize Firebase Analytics:', error);
      }

      return this.isInitialized;
    })();

    return this.initPromise;
  }

  /**
   * Schedules user-ID generation for browser idle time (with a setTimeout
   * fallback), so the ID is already cached in localStorage before any hot
   * path (e.g. a form submit) reads it synchronously via `getUserId()`.
   */
  private warmUserIdAtIdle(): void {
    if (typeof window === 'undefined') return;

    const scheduleIdle: (callback: () => void) => void =
      typeof window.requestIdleCallback === 'function'
        ? (callback) => window.requestIdleCallback(callback)
        : (callback) => window.setTimeout(callback, 0);

    scheduleIdle(() => {
      try {
        getUserId();
      } catch (error) {
        logWarn('Failed to warm analytics user ID:', error);
      }
    });
  }

  /**
   * Sets the user ID in Firebase Analytics once per session. Uses a
   * persistent anonymous identifier (no PII) and handles errors gracefully.
   */
  private setUserId(analyticsInstance: Analytics): void {
    if (this.userId) {
      // Already set in this session
      return;
    }

    try {
      const userId = getUserId();
      setUserId(analyticsInstance, userId);
      this.userId = userId;
      logDebug('Analytics user ID set', {
        userId,
      });
    } catch (error) {
      logWarn('Failed to set analytics user ID:', error);
    }
  }

  /**
   * Resets the analytics user ID; a new one is generated on the next event.
   * Useful for privacy compliance (GDPR, CCPA right to be forgotten).
   */
  public resetUserId(): void {
    clearUserId();
    this.userId = null;
    logDebug(
      'Analytics user ID reset - new ID will be generated on next event',
    );
  }

  /**
   * Enables or disables bot detection filtering for analytics events.
   * When enabled (default), events from detected bots are filtered out.
   */
  public setBotDetectionEnabled(enabled: boolean): void {
    this.featureFlags.botDetectionEnabled = enabled;
  }

  /** Checks if the current session is likely a bot. */
  private isBotSession(): ComprehensiveBotDetectionResult | undefined {
    if (!this.featureFlags.botDetectionEnabled) {
      return undefined;
    }

    const botCheck = performComprehensiveBotAnalysis();
    return botCheck;
  }

  /** Logs a bot detection event with feature flag-aware behavior. */
  private logBotDetectionEvent(
    analyticsInstance: Analytics,
    botCheck: ComprehensiveBotDetectionResult,
  ): void {
    const botDetectionKey = 'bot_detected_logged';
    const shouldLogEvent =
      this.featureFlags.allowRepeatedBotDetectionEvents ||
      !sessionStorage.getItem(botDetectionKey);

    if (!shouldLogEvent) return;

    logEvent(analyticsInstance, 'bot_detected', {
      bot_type: botCheck.botType || 'unknown',
      bot_confidence: Math.round(botCheck.confidence * 100),
      user_agent_prefix: botCheck.userAgent?.substring(
        0,
        BOT_USER_AGENT_PREFIX_LENGTH,
      ),
      environment: this.environment,
    });

    // Only set session storage if repeated events are not allowed (production behavior)
    if (!this.featureFlags.allowRepeatedBotDetectionEvents) {
      sessionStorage.setItem(botDetectionKey, 'true');
    }
  }

  /**
   * Tracks an analytics event with optional bot detection filtering.
   *
   * Automatically enriches events with bot detection metadata (`is_bot`,
   * `bot_confidence`, `interaction_score`) plus the environment tag. Events
   * from detected bots are skipped unless `skipBotCheck` is true.
   */
  public async trackEvent(
    event: AnalyticsEvent,
    skipBotCheck = false,
  ): Promise<void> {
    // Idempotent: shares the memoized init promise, so this never kicks off
    // a fresh initialization per event.
    await this.initialize();

    try {
      const analyticsInstance = await analytics;
      if (!analyticsInstance) return;

      // Ensure user ID is set (in case initialization was skipped or failed)
      this.setUserId(analyticsInstance);

      // Perform bot detection (called once per event)
      const botCheck = this.isBotSession();

      // Skip tracking if detected as bot (unless explicitly bypassed)
      if (!skipBotCheck && botCheck?.isBot) {
        logDebug('Skipping analytics event - bot detected:', {
          event: event.event_name,
          botType: botCheck.botType,
          confidence: botCheck.confidence,
          environment: this.environment,
        });

        // Log bot detection for analysis
        this.logBotDetectionEvent(analyticsInstance, botCheck);
        return;
      }

      // Track normal events with bot detection metadata for analysis
      const enrichedParams = {
        ...event.event_parameters,
        // Add bot detection info as custom parameters (for filtering later)
        is_bot: botCheck?.isBot,
        bot_confidence:
          botCheck?.confidence && Math.round((botCheck.confidence ?? 0) * 100),
        interaction_score:
          botCheck?.interactionScore &&
          Math.round(botCheck.interactionScore * 100),
        // Add environment tag to all events for filtering
        environment: this.environment,
      };

      logEvent(analyticsInstance, event.event_name, enrichedParams);
    } catch (error) {
      logWarn('Failed to track analytics event:', error);
    }
  }

  /** Tracks a page view as a custom `screen_view` event. */
  public trackPageView(page: string, title?: string): void {
    void this.trackEvent({
      event_name: 'screen_view',
      event_parameters: {
        firebase_screen: title || page,
      },
    });
  }
}

// Export singleton instance
export const analyticsService = AnalyticsService.getInstance();
